import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import { HandCoins, Clock, CheckCircle2, AlertTriangle, FileText } from "lucide-react";

const proofUrl = (proof) => proof?.storage_path ? `${API}/or-payments/file?path=${encodeURIComponent(proof.storage_path)}&auth=${localStorage.getItem("tcc_token") || ""}` : null;

export function OrStatusCard({ claim }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const nav = useNavigate();
  useEffect(() => { if (claim?.id) api.get(`/or-payments/by-claim/${claim.id}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [claim?.id]);
  if (err || !d) return null; // hidden for roles without access (403) or while loading

  const goPay = () => nav(`/or-payments?claim=${claim.id}&plate=${encodeURIComponent(claim.license_plate || "")}&wo=${encodeURIComponent(claim.wo_number || "")}`);
  const PayBtn = ({ id = "or-pay-btn" }) => (
    <button data-testid={id} onClick={goPay} className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 py-2.5 font-bold text-white shadow-sm hover:bg-orange-600">
      <HandCoins size={17} /> BAYAR OR
    </button>
  );
  const orLine = d.or_state && (
    <div data-testid="or-nominal-info" className="mb-2 text-[12px] text-gray-600">
      {d.or_state === "BELUM_DIKONFIRMASI" ? <span className="font-semibold text-amber-700">OR Belum Dikonfirmasi</span>
        : d.or_state === "LUNAS_HISTORIS" ? <>OR dibayar {formatRupiah(d.or_paid)} (data lama · nominal polis tidak tercatat)</>
        : <>Nominal OR {formatRupiah(d.or_total)} · dibayar {formatRupiah(d.or_paid)} · sisa <b>{formatRupiah(d.or_outstanding)}</b></>}
    </div>
  );
  // Normalize legacy OR statuses (complex-flow leftovers) so the card never mislabels/crashes
  const st = ({ WAITING_APPROVAL: "PENDING_APPROVAL" }[d.status]) || d.status;

  const Head = ({ tone, Icon, title }) => (
    <div className="mb-2 flex items-center gap-2">
      <span className={`flex h-7 w-7 items-center justify-center rounded-md ${tone}`}><Icon size={15} /></span>
      <span className="font-heading text-sm font-bold text-gray-800">Status Pembayaran OR</span>
      <span className="ml-auto text-[11px] font-semibold text-gray-400">{title}</span>
    </div>
  );
  const Line = ({ k, v }) => v != null && v !== "" ? <div className="flex justify-between text-[12px]"><span className="text-gray-500">{k}</span><span className="font-semibold text-gray-800">{v}</span></div> : null;
  const proofLink = proofUrl(d.proof) && <a data-testid="or-card-proof" href={proofUrl(d.proof)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:underline"><FileText size={13} />Lihat Bukti</a>;

  if (!d.exists) {
    return (
      <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50/60 p-3" data-testid="or-status-card">
        <Head tone="bg-orange-100 text-orange-600" Icon={HandCoins} title="BELUM DIBAYAR" />
        {orLine}
        {d.can_submit ? <PayBtn /> : <p className="text-[12px] text-gray-500">Pembayaran OR belum diajukan.</p>}
      </div>
    );
  }
  if (st === "PENDING_APPROVAL" || st === "APPROVED" || (st === "PAID" && !d.cash_txn_id)) {
    const t = st === "APPROVED" ? "APPROVED · BELUM ADA DANA" : st === "PAID" ? "PAID · MENUNGGU VERIFIKASI" : "MENUNGGU APPROVAL";
    return (
      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="or-status-card">
        <Head tone="bg-amber-100 text-amber-600" Icon={Clock} title={t} />
        <div data-testid="or-card-status" className="rounded-md bg-amber-500/10 px-2.5 py-1.5 text-center text-[13px] font-bold text-amber-700">{t}</div>
        <div className="mt-2 space-y-1">
          <Line k="Nominal" v={d.amount != null ? formatRupiah(d.amount) : null} />
          <Line k="Metode" v={d.method === "CASH" ? "Tunai" : d.method === "TRANSFER" ? "Transfer" : d.method} />
          <Line k="Diajukan" v={d.submitted_at} />
          <Line k="Oleh" v={d.submitted_by} />
        </div>
        {proofLink}
      </div>
    );
  }
  if (st === "NEEDS_FIX" || st === "REJECTED") {
    return (
      <div className="mb-3 rounded-lg border border-orange-300 bg-orange-50/70 p-3" data-testid="or-status-card">
        <Head tone="bg-orange-100 text-orange-600" Icon={AlertTriangle} title={st === "REJECTED" ? "DITOLAK" : "PERLU PERBAIKAN"} />
        {(d.reject_reason || d.fix_note) && <p className="mb-2 rounded-md bg-rose-500/10 px-2.5 py-1.5 text-[12px] text-rose-700">Catatan: {d.reject_reason || d.fix_note}</p>}
        {d.can_submit ? (
          <button data-testid="or-fix-btn" onClick={goPay} className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 py-2.5 font-bold text-white hover:bg-orange-600">
            <HandCoins size={17} /> PERBAIKI PENGAJUAN OR
          </button>
        ) : <p className="text-[12px] text-gray-500">Menunggu perbaikan dari SA.</p>}
      </div>
    );
  }
  if (st !== "PAID" && st !== "POSTED") return null;
  // PAID
  return (
    <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="or-status-card">
      <Head tone="bg-emerald-100 text-emerald-600" Icon={CheckCircle2} title={d.or_state === "SEBAGIAN" ? "SEBAGIAN" : "APPROVED"} />
      {orLine}
      {d.or_state === "SEBAGIAN" && d.can_submit && <div className="mb-2"><PayBtn id="or-pay-more-btn" /></div>}
      <div className="rounded-md bg-emerald-500/10 px-2.5 py-1.5 text-center text-[13px] font-bold text-emerald-700" data-testid="or-paid-badge">OR VERIFIED · POSTED{d.amount != null ? ` · ${formatRupiah(d.amount)}` : ""}</div>
      <div className="mt-2 space-y-1">
        <Line k="Tanggal" v={d.approved_at ? String(d.approved_at).slice(0, 16).replace("T", " ") : null} />
        <Line k="Metode" v={d.method === "CASH" ? "Tunai" : d.method === "TRANSFER" ? "Transfer" : d.method} />
        <Line k="Masuk ke" v={d.cash_bank} />
        <Line k="Pengaju" v={d.submitted_by} />
        <Line k="Disetujui" v={d.approved_by} />
      </div>
      {proofLink}
    </div>
  );
}
